import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {remCalc} from '../../../theme/_utils'
import {SortingTile} from './sorting-tile'
import SanitizedHtml from '../../html-sanitizer'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {debounce} from 'lodash'
import {useProducts} from '@salesforce/commerce-sdk-react'
import SortingIcons from './sorting-icons'
import {ChevronLeft, ChevronRight} from '../../icons'

export const Sorting = ({
    title = '',
    variant = 'white',
    hasSlider = false,
    heading = '',
    bodycopy = '',
    sortingGroup = [],
    buttons = [],
}) => {
    const componentUniqueKey = title.replaceAll(/ /g, '-').toLowerCase()
    const isMobile = useBreakpointValue({base: true, md: false})
    const isSlider =
        typeof window === 'undefined' ? false : hasSlider || isMobile || sortingGroup.length > 4
    const categoriesData = useGetCategoriesData()
    const swiperRef = useRef(null)
    const navigationDirection = useRef(null)

    const ids = buttons
        .filter((c) => c.button?.productsfcc?.data?.length > 0)
        .map((c) => c.button.productsfcc.data[0].id)

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: ids.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'sorting-index',
            },
        },
        {
            enabled: ids?.length > 0,
        },
    )
    const {locale} = useMultiSite()
    const buttonsProps =
        buttons?.length === 0
            ? []
            : buttons?.map((b) =>
                  getGlobalLink({
                      link: b.button,
                      categoriesData,
                      productsData,
                      locale: locale?.alias || locale?.id?.toLowerCase(),
                  }),
              )

    const intl = useIntl()
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('Sorting', {variant: variant})

    const CONTAINERS_SELECTOR = `.${componentUniqueKey} .sorting-tile`
    const RESIZABLES_SELECTOR = '.sorting-resizable'
    const DEBOUNCE_WAIT = 200
    const debouncedResize = debounce(() => {
        const containers = [...document.querySelectorAll(CONTAINERS_SELECTOR)]
        const containersResizablesHeights = containers.map((container) =>
            [...container.querySelectorAll(RESIZABLES_SELECTOR)].map((resizable) => {
                resizable.style.minHeight = null
                return resizable.offsetHeight
            }),
        )
        const maxHeights = new Array(Math.max(...containersResizablesHeights.map((c) => c.length)))
            .fill(null)
            .map((_, i) => Math.max(...containersResizablesHeights.map((h) => h[i])))
        containers.forEach((container) => {
            Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach((resizable, i) => {
                resizable.style.minHeight = `${maxHeights[i]}px` // remCalc not working
            })
        })
    }, DEBOUNCE_WAIT)

    useEffect(() => {
        if (!isSlider) {
            debouncedResize()
            window.addEventListener('resize', debouncedResize)

            return () => window.removeEventListener('resize', debouncedResize)
        }
    }, [hasSlider])

    const ArrowLeft = SortingIcons?.Arrows[variant]?.left ?? ChevronLeft
    const ArrowRight = SortingIcons?.Arrows[variant]?.right ?? ChevronRight

    return (
        <Flex className={componentUniqueKey} {...styles.main} data-testid="sorting">
            <Flex {...styles.container}>
                {heading && <Text {...styles.heading}>{heading}</Text>}
                {bodycopy && <SanitizedHtml {...styles.bodycopy} as={Box} html={bodycopy} />}
                {isSlider ? (
                    <Box
                        data-testid="sorting-swiper"
                        {...styles.emptyTiles}
                        position="relative"
                        __css={{
                            ...swiperStyles,
                            '.swiper-button-disabled': {
                                ...swiperStyles['.swiper-button-disabled'],
                                display: 'none',
                            },
                            '.swiper-slide': {
                                ...swiperStyles['.swiper-slide'],
                                height: 'auto',
                            },
                            '.swiper-pagination-bullet-active': {
                                ...swiperStyles['.swiper-pagination-bullet-active'],
                                background:
                                    variant === 'black' ? 'background.default' : 'background.dark',
                            },
                        }}
                    >
                        <Swiper
                            modules={[Navigation, Pagination, Keyboard]}
                            navigation={{
                                prevEl: `.swiper-button-prev-${componentUniqueKey}`,
                                nextEl: `.swiper-button-next-${componentUniqueKey}`,
                            }}
                            pagination={{
                                clickable: true,
                                el: `.swiper-pagination-${componentUniqueKey}`,
                                renderBullet: (index, className) => {
                                    const changeSlideLabel = intl.formatMessage({
                                        id: 'label.promo.swiper.pagination',
                                        defaultMessage: 'Go to slide',
                                    })
                                    return `<button type="button" class="${className}"
                                            aria-label="${changeSlideLabel} ${index + 1}"
                                            data-slide-index="${index}"></button>`
                                },
                            }}
                            keyboard={{
                                enabled: true,
                            }}
                            centerInsufficientSlides={true}
                            loop={isMobile ? sortingGroup.length > 1 : sortingGroup.length > 4}
                            breakpoints={{
                                0: {
                                    slidesPerView: 1,
                                    enabled: sortingGroup.length > 1,
                                    spaceBetween: 0,
                                },
                                768: {
                                    slidesPerView: 4,
                                    enabled: sortingGroup.length > 4,
                                    spaceBetween: 16,
                                },
                                1024: {
                                    slidesPerView: 4,
                                    enabled: sortingGroup.length > 4,
                                    spaceBetween: 24,
                                },
                            }}
                            onInit={() => {
                                debouncedResize()
                            }}
                            onResize={() => {
                                debouncedResize()
                            }}
                            onSlideChange={(swiper) => {
                                const bullets = document.querySelectorAll(
                                    `.swiper-pagination-${componentUniqueKey} .swiper-pagination-bullet`,
                                )
                                bullets.forEach((bullet, idx) => {
                                    if (idx === swiper.activeIndex) {
                                        bullet.setAttribute('aria-current', 'true')
                                    } else {
                                        bullet.removeAttribute('aria-current')
                                    }
                                })
                            }}
                        >
                            {sortingGroup.map((tile, tIndex) => (
                                <SwiperSlide key={`swiper-tile-${componentUniqueKey}-${tIndex}`}>
                                    <SortingTile
                                        key={`sorting-tile-${componentUniqueKey}-${tIndex}`}
                                        tile={tile}
                                        variant={variant}
                                    />
                                </SwiperSlide>
                            ))}
                            <>
                                <ArrowLeft
                                    className={`swiper-button-prev swiper-button-prev-${componentUniqueKey}`}
                                    aria-label="swiper button prev icon"
                                    tabIndex={0}
                                    onClick={() => {
                                        navigationDirection.current = 'prev'
                                        swiperRef.current?.slidePrev()
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            navigationDirection.current = 'prev'
                                            swiperRef.current?.slidePrev()
                                        }
                                    }}
                                    position="absolute"
                                    zIndex={10}
                                    {...styles.chevronIcon}
                                    left={0}
                                />

                                <Swiper
                                    modules={[Navigation, Pagination, Keyboard]}
                                    navigation={{
                                        prevEl: `.swiper-button-prev-${componentUniqueKey}`,
                                        nextEl: `.swiper-button-next-${componentUniqueKey}`,
                                    }}
                                    pagination={{
                                        clickable: true,
                                        el: `.swiper-pagination-${componentUniqueKey}`,
                                    }}
                                    keyboard={{
                                        enabled: true,
                                    }}
                                    centerInsufficientSlides={true}
                                    loop={
                                        isMobile ? sortingGroup.length > 1 : sortingGroup.length > 4
                                    }
                                    breakpoints={{
                                        0: {
                                            slidesPerView: 1,
                                            enabled: sortingGroup.length > 1,
                                            spaceBetween: 0,
                                        },
                                        768: {
                                            slidesPerView: 4,
                                            enabled: sortingGroup.length > 4,
                                            spaceBetween: 16,
                                        },
                                        1024: {
                                            slidesPerView: 4,
                                            enabled: sortingGroup.length > 4,
                                            spaceBetween: 24,
                                        },
                                    }}
                                    onInit={(swiper) => {
                                        swiperRef.current = swiper
                                        debouncedResize()

                                        const actualSlidesPerView =
                                            Math.floor(swiper.params.slidesPerView) || 1
                                        swiper.slides.forEach((slide, index) => {
                                            const isVisible =
                                                index >= swiper.activeIndex &&
                                                index < swiper.activeIndex + actualSlidesPerView
                                            const focusableElements = slide.querySelectorAll(
                                                'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                                            )
                                            focusableElements.forEach((el) => {
                                                el.setAttribute('tabindex', isVisible ? '0' : '-1')
                                            })
                                        })
                                    }}
                                    onResize={() => {
                                        debouncedResize()
                                    }}
                                    onSlideChange={(swiper) => {
                                        const actualSlidesPerView =
                                            Math.floor(swiper.params.slidesPerView) || 1
                                        swiper.slides.forEach((slide, index) => {
                                            const isVisible =
                                                index >= swiper.activeIndex &&
                                                index < swiper.activeIndex + actualSlidesPerView
                                            const focusableElements = slide.querySelectorAll(
                                                'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                                            )
                                            focusableElements.forEach((el) => {
                                                el.setAttribute('tabindex', isVisible ? '0' : '-1')
                                            })
                                        })

                                        if (navigationDirection.current) {
                                            setTimeout(() => {
                                                let targetSlideIndex

                                                if (navigationDirection.current === 'next') {
                                                    const lastVisibleIndex = Math.min(
                                                        swiper.activeIndex +
                                                            actualSlidesPerView -
                                                            1,
                                                        swiper.slides.length - 1,
                                                    )
                                                    targetSlideIndex = lastVisibleIndex
                                                } else if (navigationDirection.current === 'prev') {
                                                    targetSlideIndex = swiper.activeIndex
                                                }

                                                const targetSlide = swiper.slides[targetSlideIndex]
                                                if (targetSlide) {
                                                    const focusableElement =
                                                        targetSlide.querySelector(
                                                            'a[tabindex="0"], button[tabindex="0"]',
                                                        )
                                                    if (focusableElement) {
                                                        focusableElement.focus()
                                                    }
                                                }

                                                navigationDirection.current = null
                                            }, 150)
                                        }
                                    }}
                                >
                                    {sortingGroup.map((tile, tIndex) => (
                                        <SwiperSlide
                                            key={`swiper-tile-${componentUniqueKey}-${tIndex}`}
                                        >
                                            <SortingTile
                                                key={`sorting-tile-${componentUniqueKey}-${tIndex}`}
                                                tile={tile}
                                                variant={variant}
                                            />
                                        </SwiperSlide>
                                    ))}

                                    <Box
                                        className={`swiper-pagination-${componentUniqueKey}`}
                                        py={remCalc(24)}
                                    />
                                </Swiper>

                                <ArrowRight
                                    className={`swiper-button-next swiper-button-next-${componentUniqueKey}`}
                                    aria-label="swiper button next icon"
                                    tabIndex={0}
                                    onClick={() => {
                                        navigationDirection.current = 'next'
                                        swiperRef.current?.slideNext()
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            navigationDirection.current = 'next'
                                            swiperRef.current?.slideNext()
                                        }
                                    }}
                                    position="absolute"
                                    zIndex={10}
                                    {...styles.chevronIcon}
                                    right={0}
                                />
                            </>
                        </Swiper>
                    </Box>
                ) : (
                    <Box
                        data-testid="sorting-noswiper"
                        {...styles.emptyTiles}
                        {...styles.noswipertiles}
                    >
                        {sortingGroup.map((tile, tIndex) => (
                            <Box
                                key={`box-tile-${componentUniqueKey}-${tIndex}`}
                                {...styles.noswipertile}
                            >
                                <SortingTile
                                    key={`sorting-tile-${componentUniqueKey}-${tIndex}`}
                                    styles={styles}
                                    tile={tile}
                                    variant={variant}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
                {buttonsProps.length > 0 && (
                    <Flex {...styles.ctaContainer}>
                        {buttonsProps.map((b, bIndex) => {
                            const bType = bIndex === 0 ? 'primary' : 'secondary'
                            const bVariant = `${variant === 'black' ? 'inverse-' : ''}${bType}`
                            const bProps = b.isAbsolute
                                ? {
                                      as: 'a',
                                      href: b.url,
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                  }
                                : {
                                      as: Link,
                                      to: b.url || '',
                                  }

                            return b.label && b.url ? (
                                <Button
                                    key={`sorting-button-${componentUniqueKey}-${bIndex}`}
                                    variant={bVariant}
                                    {...styles.ctas}
                                    {...bProps}
                                >
                                    {b.label}
                                </Button>
                            ) : null
                        })}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

Sorting.propTypes = {
    title: PropTypes.string,
    variant: PropTypes.string,
    hasSlider: PropTypes.bool,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            image: PropTypes.shape(),
            features: PropTypes.arrayOf(PropTypes.shape({feature: PropTypes.string})),
            button: PropTypes.shape(),
        }),
    ),
    buttons: PropTypes.arrayOf(PropTypes.shape()),
}
